/**
 * Cometa Taller · Lógica Interactiva del Configurador (assets/src/configurador.js)
 *
 * Maneja los 4 pasos interactivos, la reactividad de la vista previa en vivo,
 * el cambio dinámico del color de fondo de la tarjeta de previsualización
 * y la actualización del resumen de compra sin dependencias externas.
 *
 * @package CometaTaller
 */

export function initConfigurador() {
  const container = document.getElementById('ct-configurador-app');
  if (!container) return;

  // Elementos del DOM
  const previewCard = document.getElementById('ct-preview-card');
  const previewPuntos = document.getElementById('preview-puntos');
  const previewNombreBase = document.getElementById('preview-nombre-base');
  const previewSubDetalle = document.getElementById('preview-sub-detalle');
  const previewFilamentos = document.getElementById('preview-filamentos');
  const previewPrecio = document.getElementById('preview-precio');
  const previewHoras = document.getElementById('preview-horas');
  const previewPiezas = document.getElementById('preview-piezas');

  // Inputs ocultos del formulario
  const inputBase = document.getElementById('input_base');
  const inputTapa = document.getElementById('input_tapa');
  const inputPaleta = document.getElementById('input_paleta');
  const inputGrabadoActivo = document.getElementById('input_grabado_activo');
  const inputGrabadoTexto = document.getElementById('ct_config_grabado_texto');
  const btnToggleGrabado = document.getElementById('btn-toggle-grabado');
  const labelGrabado = document.getElementById('label-grabado');

  // Colecciones de tejas
  const tilesBase = container.querySelectorAll('.ct-config-tile--base');
  const tilesTapa = container.querySelectorAll('.ct-config-tile--tapa');
  const tilesPaleta = container.querySelectorAll('.ct-config-tile--paleta');

  // Estado reactivo inicial
  const state = {
    base: inputBase ? inputBase.value : 'tecla',
    baseNombre: 'Clicker Tecla',
    precio: 32000,
    horas: '3 h 30',
    piezas: '5 piezas',
    tapa: inputTapa ? inputTapa.value : 'cactus',
    tapaNombre: 'Cactus',
    paleta: inputPaleta ? inputPaleta.value : 'neon',
    paletaNombre: 'Neón',
    filamentos: 'Violeta Interestelar · Magenta · Sunshine Yellow',
    fondoPreview: '#D9C9F5',
    c1: '#4A2A8A',
    c2: '#E5007E',
    c3: '#F9E069',
    conGrabado: inputGrabadoActivo ? inputGrabadoActivo.value === '1' : true,
  };

  // Helper de formato de moneda colombiana
  function formatearPrecio(val) {
    const num = Number(val) || 0;
    return '$ ' + num.toLocaleString('es-CO');
  }

  // Actualizar la interfaz y previsualización viva
  function render() {
    // 1. Fondo dinámico de la tarjeta de previsualización
    if (previewCard && state.fondoPreview) {
      previewCard.style.backgroundColor = state.fondoPreview;
    }

    // 2. Puntos cromáticos de la paleta
    if (previewPuntos) {
      const puntos = previewPuntos.querySelectorAll('.ct-config-punto-sm');
      if (puntos.length >= 3) {
        puntos[0].style.backgroundColor = state.c1;
        puntos[1].style.backgroundColor = state.c2;
        puntos[2].style.backgroundColor = state.c3;
      }
    }

    // 3. Títulos y etiquetas del producto
    if (previewNombreBase) {
      previewNombreBase.textContent = state.baseNombre;
    }
    if (previewSubDetalle) {
      previewSubDetalle.textContent = `Tapa ${state.tapaNombre} · paleta ${state.paletaNombre}`;
    }
    if (previewFilamentos) {
      previewFilamentos.textContent = state.filamentos;
    }

    // 4. Métricas y precio en el resumen Sol
    if (previewPrecio) {
      previewPrecio.textContent = formatearPrecio(state.precio);
    }
    if (previewHoras) {
      previewHoras.textContent = `${state.horas} de máquina`;
    }
    if (previewPiezas) {
      previewPiezas.textContent = `${state.piezas} en total`;
    }

    // 5. Estado de grabado
    if (btnToggleGrabado && labelGrabado) {
      if (state.conGrabado) {
        btnToggleGrabado.classList.add('is-active');
        btnToggleGrabado.setAttribute('aria-pressed', 'true');
        labelGrabado.textContent = 'Con grabado';
        if (inputGrabadoTexto) {
          inputGrabadoTexto.disabled = false;
          inputGrabadoTexto.style.opacity = '1';
        }
      } else {
        btnToggleGrabado.classList.remove('is-active');
        btnToggleGrabado.setAttribute('aria-pressed', 'false');
        labelGrabado.textContent = 'Sin grabado';
        if (inputGrabadoTexto) {
          inputGrabadoTexto.disabled = true;
          inputGrabadoTexto.style.opacity = '0.5';
        }
      }
    }

    // 6. Sincronizar inputs ocultos
    if (inputBase) inputBase.value = state.base;
    if (inputTapa) inputTapa.value = state.tapa;
    if (inputPaleta) inputPaleta.value = state.paleta;
    if (inputGrabadoActivo) inputGrabadoActivo.value = state.conGrabado ? '1' : '0';
  }

  // Listener: Paso 1 (Base)
  tilesBase.forEach((tile) => {
    tile.addEventListener('click', () => {
      tilesBase.forEach((t) => {
        t.classList.remove('is-selected');
        t.setAttribute('aria-checked', 'false');
      });
      tile.classList.add('is-selected');
      tile.setAttribute('aria-checked', 'true');

      state.base = tile.dataset.val;
      state.baseNombre = tile.dataset.nombre || 'Clicker';
      state.precio = Number(tile.dataset.precio) || 32000;
      state.horas = tile.dataset.horas || '3 h 30';
      state.piezas = tile.dataset.piezas || '5 piezas';
      render();
    });
  });

  // Listener: Paso 2 (Tapa)
  tilesTapa.forEach((tile) => {
    tile.addEventListener('click', () => {
      tilesTapa.forEach((t) => {
        t.classList.remove('is-selected');
        t.setAttribute('aria-checked', 'false');
      });
      tile.classList.add('is-selected');
      tile.setAttribute('aria-checked', 'true');

      state.tapa = tile.dataset.val;
      state.tapaNombre = tile.dataset.nombre || 'Cactus';
      render();
    });
  });

  // Listener: Paso 3 (Paleta)
  tilesPaleta.forEach((tile) => {
    tile.addEventListener('click', () => {
      tilesPaleta.forEach((t) => {
        t.classList.remove('is-selected');
        t.setAttribute('aria-checked', 'false');
      });
      tile.classList.add('is-selected');
      tile.setAttribute('aria-checked', 'true');

      state.paleta = tile.dataset.val;
      state.paletaNombre = tile.dataset.nombre || 'Neón';
      state.filamentos = tile.dataset.filamentos || '';
      state.fondoPreview = tile.dataset.fondo || '#D9C9F5';
      state.c1 = tile.dataset.c1 || '#4A2A8A';
      state.c2 = tile.dataset.c2 || '#E5007E';
      state.c3 = tile.dataset.c3 || '#F9E069';
      render();
    });
  });

  // Listener: Paso 4 (Toggle Grabado)
  if (btnToggleGrabado) {
    btnToggleGrabado.addEventListener('click', () => {
      state.conGrabado = !state.conGrabado;
      render();
    });
  }

  // Inicialización
  render();
}
